import { BookListItem, CustomPaginator } from "components";
import { Container } from "layouts";
import PaddingX from "layouts/PaddingX";
import { useMemo, useState } from "react";
import useMyLibraryFilters from "./useMyLibraryFilters";
import useMyLibrary from "api/BookResource/useMyLibrary";
import BooksLoader from "pages2/Books/BooksLoader";
import { Link, useHistory } from "react-router-dom";
import useCheckIfUserIsSubscribed from "api/SubscriptionsResource/useCheckIfUserIsSubscribed";
import { IoDocumentsOutline, IoLibraryOutline, IoStatsChartOutline } from "react-icons/io5";
import MyLibraryLayout from "./MyLibraryLayout";
import ReadOnAppBanner from "components/v2/ReadOnAppBanner";
import ReadOnAppModal from "components/v2/ReadOnAppModal";

const MyLibrary = () => {
	const filters = useMyLibraryFilters();
	const history = useHistory();
	
	// State for the "Read on App" modal
	const [showReadOnAppModal, setShowReadOnAppModal] = useState(false);
	const [selectedBook, setSelectedBook] = useState(null);

	const { data: isSubscribed, isLoading: isLoadingSubscribed } = useCheckIfUserIsSubscribed();

	const { data: libraryBooks, isLoading: isLoadingLibraryBooks } = useMyLibrary({
		page: filters?.page,
		rows_per_page: filters?.rowsPerPage,
	});
	
	// Handle book click - show modal instead of navigating
	const handleBookClick = (book) => {
		setSelectedBook(book);
		setShowReadOnAppModal(true);
	};
	
	// Handle view book details from modal
	const handleViewBookDetails = () => {
		if (selectedBook) {
			setShowReadOnAppModal(false);
			history.push(`/book/${selectedBook.id}`);
		}
	};

	const disableRowsPerPageChanger = useMemo(() => {
		if (
			parseInt(filters?.page) === parseInt(libraryBooks?.data?.total_pages) &&
			parseInt(filters?.page) !== 1
		) {
			return true;
		}
		return false;
	}, [filters?.page, libraryBooks]);

	// Filter and sort books
	const filteredAndSortedBooks = useMemo(() => {
		if (!libraryBooks?.data?.records) return [];

		let filtered = libraryBooks.data.records;

		return filtered;
	}, [libraryBooks?.data?.records]);

	// Calculate library statistics
	const libraryStats = useMemo(() => {
		if (!libraryBooks?.data?.records) {
			return { total: 0, purchased: 0, assigned: 0, subscription: 0 };
		}

		const books = libraryBooks.data.records;
		return {
			total: books.length,
			purchased: books.filter((book) => book.is_purchased).length,
			assigned: books.filter((book) => book.assigned).length,
			subscription: books.filter((book) => book.in_library).length,
		};
	}, [libraryBooks?.data?.records]);

	const handleChangeRowsPerPage = (value) => {
		if (value === filters?.rowsPerPage) return;
		filters.changeRowsPerPage(value);
	};

	return (
		<Container>
			<div className="min-h-screen ">
				<PaddingX>
					<MyLibraryLayout>
						{/* Header Section */}
						<div className="bg-white rounded-lg py-6 ">
							<div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
								<div className="flex items-center gap-3">
									<div className="p-2 bg-blue-100 rounded-lg">
										<IoLibraryOutline className="w-6 h-6 text-akgreener" />
									</div>
									<div>
										<h1 className="text-2xl font-bold text-gray-900">My Library</h1>
										<p className="text-gray-600">Your personal collection of books</p>
									</div>
								</div>

								{/* Library Stats */}
								{!isLoadingLibraryBooks && libraryBooks?.data?.records && (
									<div className="hidden sm:flex gap-4 text-sm">
										<div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg">
											<IoStatsChartOutline className="w-4 h-4 text-green-600" />
											<span className="font-medium text-green-800">{libraryStats.total} Total</span>
										</div>

										{libraryStats.purchased > 0 && (
											<div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg text-xs">
												{/* <IoTimeOutline className="w-4 h-4 text-blue-600" /> */}
												<span className="font-medium text-blue-800">
													{libraryStats.purchased} Purchased
												</span>
											</div>
										)}

										{libraryStats.subscription > 0 && (
											<div className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg text-xs">
												{/* <IoTimeOutline className="w-4 h-4 text-red-600" /> */}
												<span className="font-medium text-red-800">
													{libraryStats.subscription} Subscribed
												</span>
											</div>
										)}
									</div>
								)}
						</div>
					</div>

						{/* Read on App Info Banner */}
						<ReadOnAppBanner className="mb-4" />

						{/* Results Section */}
						<div className="bg-white rounded-lg shadow-sm border border-gray-200">
							{/* Results Header */}
							<div className="px-6 py-4 border-b border-gray-200">
								<div className="flex flex-row items-center sm:justify-between justify-end gap-2">
									<div className="hidden sm:block">
										<p className="text-sm text-gray-600">
											Showing {filteredAndSortedBooks.length} of{" "}
											{libraryBooks?.data?.total_records || 0} books
										</p>
									</div>

									<div className="flex items-center gap-2">
										<span className="text-sm text-gray-600">Show</span>
										<select
											name="rowsPerPage"
											id="rowsPerPage"
											value={filters.rowsPerPage}
											onChange={(e) => handleChangeRowsPerPage(e.target.value)}
											disabled={disableRowsPerPageChanger}
											className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
										>
											<option value="10">10</option>
											<option value="20">20</option>
											<option value="40">40</option>
											<option value="50">50</option>
										</select>
									</div>
								</div>
							</div>

							{/* Content */}
							<div className="p-6">
								{(function () {
									if (isLoadingLibraryBooks || isLoadingSubscribed) {
										return (
											<>
												<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
													{Array.from({ length: 18 }, (_, i) => (
														<BooksLoader key={i} />
													))}
												</div>
											</>
										);
									}

									if (filteredAndSortedBooks.length === 0) {
										return (
											<div className="flex flex-col items-center justify-center py-12">
												<div className="p-4 bg-gray-100 rounded-full mb-4">
													<IoDocumentsOutline size={64} className="text-gray-400" />
												</div>
												<h3 className="text-lg font-semibold text-gray-900 mb-2">
													Your library is empty
												</h3>
												<p className="text-gray-600 text-center mb-6 max-w-md">
													Start building your library by purchasing books, getting assigned books,
													or subscribing to a plan.
												</p>
												<Link
													to="/books"
													className="inline-flex items-center gap-2 px-6 py-3 bg-akgreener text-white font-medium rounded-lg hover:bg-akgreen transition-colors"
												>
													<IoLibraryOutline className="w-5 h-5" />
													Browse Books
												</Link>
											</div>
										);
									}

									return (
										<>
											<div
												className={`grid gap-4 ${"grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 "}`}
											>
												{filteredAndSortedBooks.map((book) => (
													<BookListItem
														key={book.id}
														coverUrl={book?.book_cover?.url}
														title={book?.title}
														author={book?.author}
														description={book?.description}
														price={book?.price}
														zwl_price={book?.zwl_price}
														credits={book.credits}
														publisher={
															book?.user?.publishers[0]?.name ||
															`${book?.user?.first_name} ${book?.user?.last_name}`
														}
														showPrice={false}
														reviews={book.ratings}
														bookId={book.id}
														sellable={book.sellable}
														rating={book.rating}
														isbn={book.rating}
														isPurchased={book.is_purchased}
														inLibrary={book.in_library}
														subscription={book.subscription}
														hasSubscription={isSubscribed?.data}
														category={book?.category?.name}
														isFree={book?.isFree}
														genres={book?.genre?.map((genre) => [genre.id, genre.name])}
														onClick={() => handleBookClick(book)}
														showReadOnAppBadge={true}
													/>
												))}
											</div>

											{/* Pagination */}
											{libraryBooks?.data?.total_pages > 1 && (
												<div className="mt-8 pt-6 border-t border-gray-200">
													<CustomPaginator
														firstPage={filters.firstPage}
														setFirstPage={filters.changeFirstPage}
														rowsPerPage={filters.rowsPerPage}
														setRowsPerPage={filters.changeRowsPerPage}
														totalRecords={libraryBooks?.data?.total_records}
														setPage={filters.changePage}
														showRowsPerPageDropdown={false}
													/>
												</div>
											)}
										</>
									);
								})()}
							</div>
						</div>
					</MyLibraryLayout>
				</PaddingX>
			</div>
			
			{/* Read on App Modal */}
			<ReadOnAppModal
				visible={showReadOnAppModal}
				onClose={() => {
					setShowReadOnAppModal(false);
					setSelectedBook(null);
				}}
				bookTitle={selectedBook?.title}
				onViewDetails={handleViewBookDetails}
			/>
		</Container>
	);
};

export default MyLibrary;
